import { Redirect, RouteComponentProps, Router } from '@gatsbyjs/reach-router';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import React, { FC, useMemo } from 'react';
import { getCookieValue } from '../../helpers/styles/cookie';
import { ABExperimentsRouterQueryData } from './types/ABExperimentsRouter.types';

type ABExperimentsRouterProps = {
  cookieName?: string;
  children?: React.ReactNode;
};

const EXPERIMENT_B_WEIGHT = 50;

const ABExperimentsRouter: FC<ABExperimentsRouterProps> = ({
  cookieName,
  children,
}) => {
  if (!cookieName) {
    return <>{children}</>;
  }

  const data = useStaticQuery<ABExperimentsRouterQueryData>(graphql`
    query {
      allContentfulPageArticle(
        filter: {
          node_locale: { eq: "en" }
          abExperiments: { elemMatch: { id: { ne: null } } }
        }
      ) {
        nodes {
          id
          slug
          abExperiments {
            slug
          }
        }
      }

      contentfulSectionFilterableArticleIndex(
        pageName: { eq: "KNOWLEDGE_CENTRE" }
      ) {
        slug
      }
    }
  `);

  const { nodes: articlesWithABExperiments } = data.allContentfulPageArticle;
  const { slug: articleIndexSlug } =
    data.contentfulSectionFilterableArticleIndex;

  const redirects = useMemo(
    () =>
      articlesWithABExperiments.map(article => {
        const cookieValue = getCookieValue(cookieName || '');

        if (!cookieValue) {
          return null;
        }

        const value = parseInt(cookieValue, 10);

        const isVariantB = value > EXPERIMENT_B_WEIGHT;

        const fromSlug = isVariantB
          ? article.slug
          : article.abExperiments[0].slug;
        const toSlug = isVariantB
          ? article.abExperiments[0].slug
          : article.slug;

        const pathFromRedirect = withPrefix(
          `/${articleIndexSlug}/${fromSlug}/`
        );

        const pathToRedirect = withPrefix(`/${articleIndexSlug}/${toSlug}/`);

        return { id: article.id, pathFromRedirect, pathToRedirect };
      }),
    [articlesWithABExperiments, articleIndexSlug, cookieName]
  );

  const ChildrenRoute = (_props: RouteComponentProps) => (
    <React.Fragment>{children}</React.Fragment>
  );

  return (
    <Router>
      {redirects.map(
        redirect =>
          redirect && (
            <Redirect
              key={redirect.id}
              from={redirect.pathFromRedirect}
              to={redirect.pathToRedirect}
              noThrow
            />
          )
      )}
      <ChildrenRoute default />
    </Router>
  );
};
export default ABExperimentsRouter;
